import React, { useMemo, useCallback } from 'react';
import { Card, Select, Space, Typography, Button, Empty, message } from 'antd';
import { SwapOutlined, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { exportToPdf } from '../../../components/ReportExport';
import {
  DIMENSION_FIELDS,
  MEASURE_OPTIONS,
  MEASURE_KEYS,
  DATE_GRANULARITIES,
  computeCommercePivot,
  computeCommerceTotals
} from '../utils/commercePivotUtils';

const { Text } = Typography;
const { Option } = Select;

const formatMeasure = (mk, val) => {
  if (mk === MEASURE_KEYS.TOTAL_AMOUNT) {
    return Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return Number(val || 0).toLocaleString();
};

const CommercePivotTable = ({ dataSource }) => {
  const { t } = useLanguage();
  const [rowDimKey, setRowDimKey] = React.useState('customerName');
  const [colDimKey, setColDimKey] = React.useState('deliveryDate');
  const [rowDateGranularity, setRowDateGranularity] = React.useState('month');
  const [colDateGranularity, setColDateGranularity] = React.useState('month');
  const [measureKeys, setMeasureKeys] = React.useState([MEASURE_KEYS.TOTAL_QTY, MEASURE_KEYS.TOTAL_AMOUNT]);

  const parseDim = useCallback((key, dateGranularity) => {
    const field = DIMENSION_FIELDS.find((f) => f.key === key);
    if (!field) return null;
    return {
      fieldId: field.key,
      isDate: field.isDate,
      dateGranularity: field.isDate ? dateGranularity : undefined
    };
  }, []);

  const rowDim = useMemo(() => parseDim(rowDimKey, rowDateGranularity), [rowDimKey, rowDateGranularity, parseDim]);
  const colDim = useMemo(() => parseDim(colDimKey, colDateGranularity), [colDimKey, colDateGranularity, parseDim]);

  const { rows, cols, cells, measures } = useMemo(
    () => computeCommercePivot(dataSource, rowDim, colDim, measureKeys),
    [dataSource, rowDim, colDim, measureKeys]
  );

  const { rowTotals, colTotals, grandTotal } = useMemo(
    () => computeCommerceTotals(rows, cols, cells, measures),
    [rows, cols, cells, measures]
  );

  const getDimLabel = (key) => {
    const f = DIMENSION_FIELDS.find((d) => d.key === key);
    return f ? (t(f.labelKey) || f.key) : key;
  };

  const getMeasureLabel = (mk) => {
    const m = MEASURE_OPTIONS.find((o) => o.key === mk);
    return m ? (t(m.labelKey) || mk) : mk;
  };

  const handleSwap = () => {
    setRowDimKey(colDimKey);
    setColDimKey(rowDimKey);
    setRowDateGranularity(colDateGranularity);
    setColDateGranularity(rowDateGranularity);
  };

  const handleExportExcel = () => {
    try {
      const totalLabel = t('reports.commerce.pivotTotal') || 'Total';
      const headerRow1 = [''];
      const headerRow2 = [''];
      cols.forEach((ck) => {
        measures.forEach((mk) => {
          headerRow1.push(ck);
          headerRow2.push(getMeasureLabel(mk));
        });
      });
      measures.forEach((mk) => {
        headerRow1.push(totalLabel);
        headerRow2.push(getMeasureLabel(mk));
      });

      const dataRows = rows.map((rk) => {
        const row = [rk];
        cols.forEach((ck) => {
          measures.forEach((mk) => {
            row.push(cells[rk]?.[ck]?.[mk]?.value ?? '');
          });
        });
        measures.forEach((mk) => {
          row.push(rowTotals[rk]?.[mk]?.value ?? '');
        });
        return row;
      });

      const totalRow = [totalLabel];
      cols.forEach((ck) => {
        measures.forEach((mk) => {
          totalRow.push(colTotals[ck]?.[mk]?.value ?? '');
        });
      });
      measures.forEach((mk) => {
        totalRow.push(grandTotal[mk]?.value ?? '');
      });

      const ws = XLSX.utils.aoa_to_sheet([headerRow1, headerRow2, ...dataRows, totalRow]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pivot');
      XLSX.writeFile(wb, `commerce_sales_pivot_${new Date().toISOString().slice(0, 10)}.xlsx`);
      message.success(t('reports.commerce.exportSuccess'));
    } catch (err) {
      message.error(`${t('reports.commerce.exportFailed')}: ${err.message}`);
    }
  };

  const handleExportPdf = async () => {
    const flatRows = rows.map((rk) => {
      const row = { row: rk };
      cols.forEach((ck) => {
        measures.forEach((mk) => {
          row[`${ck}_${mk}`] = cells[rk]?.[ck]?.[mk]?.value ?? 0;
        });
      });
      return row;
    });
    const columns = [
      { title: getDimLabel(rowDimKey), dataIndex: 'row' },
      ...cols.flatMap((ck) =>
        measures.map((mk) => ({
          title: `${ck} ${getMeasureLabel(mk)}`,
          dataIndex: `${ck}_${mk}`
        }))
      )
    ];
    try {
      await exportToPdf(flatRows, columns, 'commerce_sales_pivot', {
        title: t('reports.commerce.salesPivot')
      });
      message.success(t('reports.commerce.exportSuccess'));
    } catch (err) {
      message.error(`${t('reports.commerce.exportFailed')}: ${err.message}`);
    }
  };

  if (!dataSource?.length) {
    return <Empty description={t('reports.commerce.noData')} style={{ padding: 60 }} />;
  }

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap align="end">
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{t('reports.commerce.pivotMeasure')}</Text>
            <Select
              mode="multiple"
              style={{ minWidth: 220 }}
              value={measureKeys}
              onChange={setMeasureKeys}
            >
              {MEASURE_OPTIONS.map((m) => (
                <Option key={m.key} value={m.key}>{t(m.labelKey) || m.key}</Option>
              ))}
            </Select>
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{t('reports.commerce.pivotRowDimension')}</Text>
            <Space.Compact>
              <Select style={{ minWidth: 160 }} value={rowDimKey} onChange={setRowDimKey}>
                {DIMENSION_FIELDS.map((f) => (
                  <Option key={f.key} value={f.key}>{t(f.labelKey) || f.key}</Option>
                ))}
              </Select>
              {DIMENSION_FIELDS.find((f) => f.key === rowDimKey)?.isDate && (
                <Select value={rowDateGranularity} onChange={setRowDateGranularity} style={{ width: 90 }}>
                  {DATE_GRANULARITIES.map((g) => (
                    <Option key={g.value} value={g.value}>{t(g.labelKey)}</Option>
                  ))}
                </Select>
              )}
            </Space.Compact>
          </div>
          <Button icon={<SwapOutlined />} onClick={handleSwap} />
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{t('reports.commerce.pivotColDimension')}</Text>
            <Space.Compact>
              <Select style={{ minWidth: 160 }} value={colDimKey} onChange={setColDimKey}>
                {DIMENSION_FIELDS.map((f) => (
                  <Option key={f.key} value={f.key}>{t(f.labelKey) || f.key}</Option>
                ))}
              </Select>
              {DIMENSION_FIELDS.find((f) => f.key === colDimKey)?.isDate && (
                <Select value={colDateGranularity} onChange={setColDateGranularity} style={{ width: 90 }}>
                  {DATE_GRANULARITIES.map((g) => (
                    <Option key={g.value} value={g.value}>{t(g.labelKey)}</Option>
                  ))}
                </Select>
              )}
            </Space.Compact>
          </div>
          <Button icon={<FileExcelOutlined />} onClick={handleExportExcel}>{t('reports.commerce.exportExcel')}</Button>
          <Button icon={<FilePdfOutlined />} onClick={handleExportPdf}>{t('reports.commerce.exportPdf')}</Button>
        </Space>
      </Card>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th rowSpan={2} style={{ border: '1px solid #f0f0f0', padding: 8, background: '#fafafa', minWidth: 180 }}>
                {getDimLabel(rowDimKey)} \ {getDimLabel(colDimKey)}
              </th>
              {cols.map((ck) => (
                <th key={ck} colSpan={measures.length} style={{ border: '1px solid #f0f0f0', padding: 8, background: '#fafafa', textAlign: 'center' }}>
                  {ck}
                </th>
              ))}
              <th colSpan={measures.length} style={{ border: '1px solid #f0f0f0', padding: 8, background: '#e6f4ff', textAlign: 'center' }}>
                {t('reports.commerce.pivotTotal')}
              </th>
            </tr>
            <tr>
              {cols.flatMap((ck) =>
                measures.map((mk) => (
                  <th key={`${ck}_${mk}`} style={{ border: '1px solid #f0f0f0', padding: 6, background: '#fafafa', fontSize: 11 }}>
                    {getMeasureLabel(mk)}
                  </th>
                ))
              )}
              {measures.map((mk) => (
                <th key={`total_${mk}`} style={{ border: '1px solid #f0f0f0', padding: 6, background: '#e6f4ff', fontSize: 11 }}>
                  {getMeasureLabel(mk)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ fontWeight: 600, background: '#f5f5f5' }}>
              <td style={{ border: '1px solid #f0f0f0', padding: 8 }}>{t('reports.commerce.pivotTotal')}</td>
              {cols.flatMap((ck) =>
                measures.map((mk) => (
                  <td key={`tot_${ck}_${mk}`} style={{ border: '1px solid #f0f0f0', padding: 8, textAlign: 'right' }}>
                    {formatMeasure(mk, colTotals[ck]?.[mk]?.value)}
                  </td>
                ))
              )}
              {measures.map((mk) => (
                <td key={`grand_${mk}`} style={{ border: '1px solid #f0f0f0', padding: 8, textAlign: 'right', background: '#e6f4ff' }}>
                  {formatMeasure(mk, grandTotal[mk]?.value)}
                </td>
              ))}
            </tr>
            {rows.map((rk) => (
              <tr key={rk}>
                <td style={{ border: '1px solid #f0f0f0', padding: 8 }}>{rk}</td>
                {cols.flatMap((ck) =>
                  measures.map((mk) => (
                    <td key={`${rk}_${ck}_${mk}`} style={{ border: '1px solid #f0f0f0', padding: 8, textAlign: 'right' }}>
                      {formatMeasure(mk, cells[rk]?.[ck]?.[mk]?.value)}
                    </td>
                  ))
                )}
                {measures.map((mk) => (
                  <td key={`${rk}_tot_${mk}`} style={{ border: '1px solid #f0f0f0', padding: 8, textAlign: 'right', background: '#fafafa' }}>
                    {formatMeasure(mk, rowTotals[rk]?.[mk]?.value)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CommercePivotTable;
